(() => {
  const BCPO_ADD_ON_ENDPOINT = 'https://obscure-escarpment-2240.herokuapp.com/add-ons';
  const CHECKOUT_TIMEOUT_MS = 8000;
  const CHECKOUT_RETRY_DELAY_MS = 500;
  const CHECKOUT_ERROR_TEXT = "We couldn't confirm your custom-option pricing. Your cart is saved. Please try Checkout again or contact us for help.";
  const TRUSTED_CHECKOUT_HOSTS = Object.freeze([
    'fredesigner.com',
    'www.fredesigner.com',
    'c5e1ec-d7.myshopify.com'
  ]);
  const GUARDED_NEGATIVE_COLOR_PRODUCT_IDS = new Set([
    '9267957629178',
    '9267981189370',
    '9267998163194'
  ]);
  const COLOR_PRICE_GUARD_VERSION = 'negative-color-v2';
  const PRIVATE_PRICE_KEYS = {
    customization: ['_fd_customization'],
    base: ['_fd_bcpo_base_cents', '_fd_base_price_cents', '_fd_base_cents'],
    addOn: [
      '_fd_bcpo_addon_cents',
      '_fd_bcpo_add_on_cents',
      '_fd_addon_price_cents',
      '_fd_add_on_price_cents',
      '_fd_addon_cents'
    ],
    configured: [
      '_fd_bcpo_configured_cents',
      '_fd_configured_price_cents',
      '_fd_configured_unit_cents',
      '_fd_configured_cents'
    ],
    currency: ['_fd_currency'],
    checksum: ['_fd_selection_checksum'],
    state: ['_fd_price_state'],
    guardVersion: ['_fd_price_guard_version'],
    colorAdjustment: ['_fd_color_adjustment_cents']
  };

  let activeConfiguredCart = null;
  let hasAppliedInitialCart = false;
  let checkoutFlight = null;
  let addOnRequestFlight = null;
  let nativeCheckoutReplay = false;

  const getActiveCurrency = () => String(
    window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'USD'
  ).toUpperCase();

  const parseUsdAddOnCents = (value, currency) => {
    if (currency !== 'USD') return null;
    const suffix = String(value || '').match(/\|\s*(.*?)\s*$/i);
    if (!suffix) return null;
    const priceText = suffix[1];
    if (!/(?:US)?\$|USD/i.test(priceText)) return null;
    const numberMatch = priceText.match(/[0-9,]+(?:\.[0-9]{1,2})?/);
    if (!numberMatch) return null;
    const magnitude = Number(numberMatch[0].replace(/,/g, ''));
    const amount = priceText.includes('-') ? -magnitude : magnitude;
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  };

  const parsePrivateCents = (value) => {
    const text = String(value == null ? '' : value).trim();
    if (!/^\d+$/.test(text)) return null;
    const cents = Number(text);
    return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
  };

  const parseSignedCents = (value) => {
    const text = String(value == null ? '' : value).trim();
    if (!/^-?\d+$/.test(text)) return null;
    const cents = Number(text);
    return Number.isSafeInteger(cents) ? cents : null;
  };

  const canonicalPropertyValue = (value) => String(value == null ? '' : value)
    .replace(/\s*\|\s*-?\s*(?:(?:US|CA|C)?\$|USD|CAD|EUR|GBP)\s*-?\s*[0-9,]+(?:\.[0-9]{1,2})?\s*$/i, '')
    .trim();

  const selectionPairsFromProperties = (properties) => Object.entries(properties || {})
    .filter(([key]) => {
      const name = String(key || '').trim();
      return name && !name.startsWith('_');
    })
    .map(([key, value]) => {
      const name = String(key || '').trim();
      const canonicalValue = canonicalPropertyValue(value);
      return canonicalValue ? `${name}=${canonicalValue}` : '';
    })
    .filter(Boolean)
    .filter((pair, index, pairs) => pairs.indexOf(pair) === index)
    .sort();

  const selectionChecksum = (pairs) => {
    const text = pairs.join('\n');
    let hash = 2166136261;
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
  };

  const readPrivatePricing = (properties, currency) => {
    const entries = Object.entries(properties || {});
    const byName = new Map(entries.map(([key, value]) => [String(key).toLowerCase(), value]));
    const read = (names, parser = parsePrivateCents) => {
      const key = names.find((name) => byName.has(name));
      if (!key) return { present: false, value: null };
      return { present: true, value: parser(byName.get(key)) };
    };
    const base = read(PRIVATE_PRICE_KEYS.base);
    const addOn = read(PRIVATE_PRICE_KEYS.addOn, parseSignedCents);
    const configured = read(PRIVATE_PRICE_KEYS.configured);
    const readText = (names) => {
      const key = names.find((name) => byName.has(name));
      if (!key) return { present: false, value: '' };
      return { present: true, value: String(byName.get(key) == null ? '' : byName.get(key)).trim() };
    };
    const customization = readText(PRIVATE_PRICE_KEYS.customization);
    const state = readText(PRIVATE_PRICE_KEYS.state);
    const propertyCurrency = readText(PRIVATE_PRICE_KEYS.currency);
    const checksum = readText(PRIVATE_PRICE_KEYS.checksum);
    const guardVersion = readText(PRIVATE_PRICE_KEYS.guardVersion);
    const colorAdjustment = read(PRIVATE_PRICE_KEYS.colorAdjustment, parseSignedCents);
    const hasAny = customization.present
      || base.present
      || addOn.present
      || configured.present
      || state.present
      || propertyCurrency.present
      || checksum.present
      || guardVersion.present
      || colorAdjustment.present;
    const amountFieldsComplete = base.present
      && addOn.present
      && configured.present
      && base.value !== null
      && addOn.value !== null
      && configured.value !== null;
    const selectionPairs = selectionPairsFromProperties(properties);
    const calculatedChecksum = selectionChecksum(selectionPairs);
    const markersComplete = customization.value === '1'
      && state.value.toLowerCase() === 'ready'
      && propertyCurrency.value.toUpperCase() === String(currency || '').toUpperCase()
      && checksum.value === calculatedChecksum;
    return {
      hasAny,
      isValid: !hasAny || (amountFieldsComplete && markersComplete),
      base,
      addOn,
      configured,
      customization,
      state,
      currency: propertyCurrency,
      checksum,
      guardVersion,
      colorAdjustment,
      calculatedChecksum,
      selectionPairs
    };
  };

  const readLegacyPricing = (properties, currency) => {
    let matched = false;
    let isValid = true;
    let addOnCents = 0;
    let nonZeroMatchCount = 0;
    Object.entries(properties || {}).forEach(([key, value]) => {
      if (String(key).startsWith('_')) return;
      if (!/\|\s*-?\s*(?:(?:US)?\$|USD)\s*-?\s*[0-9]/i.test(String(value || ''))) return;
      matched = true;
      const parsed = parseUsdAddOnCents(value, currency);
      if (!Number.isSafeInteger(parsed)) {
        isValid = false;
        return;
      }
      addOnCents += parsed;
      if (parsed !== 0) nonZeroMatchCount += 1;
    });
    return { matched, isValid, addOnCents, nonZeroMatchCount };
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const readServerCartEvidence = (root = document) => {
    const byLineKey = new Map();
    root.querySelectorAll('script[data-fd-cart-server-evidence]').forEach((node) => {
      let rows;
      try {
        rows = JSON.parse(node.textContent || '[]');
      } catch (error) {
        return;
      }
      if (!Array.isArray(rows)) return;
      rows.forEach((row) => {
        const lineKey = String(row && row.lineKey || '').trim();
        if (!lineKey
          || typeof row.customizationRequired !== 'boolean'
          || typeof row.configuredPriceStateRequired !== 'boolean'
          || typeof row.bcpoDataPresent !== 'boolean') return;
        byLineKey.set(lineKey, {
          customizationRequired: row.customizationRequired,
          configuredPriceStateRequired: row.configuredPriceStateRequired,
          bcpoDataPresent: row.bcpoDataPresent,
          productId: String(row.productId || ''),
          variantId: String(row.variantId || '')
        });
      });
    });
    return byLineKey;
  };

  const calculateConfiguredCart = (cart, serverEvidenceByLineKey = readServerCartEvidence()) => {
    const currency = String(cart && cart.currency || getActiveCurrency()).toUpperCase();
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    let addOnSubtotalCents = 0;
    let customizedItemCount = 0;
    let nonZeroAddOnLineCount = 0;
    let isValid = true;
    const validationErrors = [];
    const lines = new Map();

    items.forEach((item, index) => {
      const quantity = Number(item.quantity);
      const lineKey = String(item && item.key || '');
      const serverEvidence = serverEvidenceByLineKey.get(lineKey);
      const hasServerEvidence = Boolean(serverEvidence);
      const serverIdentityMatches = Boolean(
        serverEvidence
        && serverEvidence.productId
        && serverEvidence.variantId
        && serverEvidence.productId === String(item && item.product_id || '')
        && serverEvidence.variantId === String(item && item.variant_id || '')
      );
      const serverRequiresCustomization = Boolean(
        serverEvidence && serverEvidence.customizationRequired
      );
      const serverRequiresConfiguredPriceState = Boolean(
        serverEvidence && serverEvidence.configuredPriceStateRequired
      );
      const serverBcpoDataPresent = Boolean(serverEvidence && serverEvidence.bcpoDataPresent);
      const privatePricing = readPrivatePricing(item && item.properties, currency);
      const legacyPricing = readLegacyPricing(item && item.properties, currency);
      const usesPrivateAddOn = privatePricing.hasAny;
      const guardedColorPriceRequired = Boolean(
        serverEvidence
        && GUARDED_NEGATIVE_COLOR_PRODUCT_IDS.has(serverEvidence.productId)
      );
      const hasColorSelection = Object.entries(item && item.properties || {}).some(([key, value]) => (
        String(key || '').trim().toLowerCase() === 'color'
        && Boolean(canonicalPropertyValue(value))
      ));
      const guardedColorPriceIsValid = !guardedColorPriceRequired || (
        usesPrivateAddOn
        && privatePricing.guardVersion.present
        && privatePricing.guardVersion.value === COLOR_PRICE_GUARD_VERSION
        && privatePricing.colorAdjustment.present
        && Number.isSafeInteger(privatePricing.colorAdjustment.value)
        && hasColorSelection
      );
      const hasBcpoPricingEvidence = privatePricing.hasAny || legacyPricing.matched;
      const isCustomized = serverRequiresCustomization
        || (serverBcpoDataPresent && hasBcpoPricingEvidence);
      const authoritativeBaseUnitCents = [
        item && item.original_price,
        item && item.price,
        item && item.final_price
      ]
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map(Number)
        .find((value) => Number.isSafeInteger(value) && value >= 0);
      const baseUnitCents = usesPrivateAddOn
        ? privatePricing.base.value
        : Number(item.final_price);
      const addOnUnitCents = usesPrivateAddOn
        ? privatePricing.addOn.value
        : legacyPricing.addOnCents;
      const configuredUnitCents = usesPrivateAddOn
        ? privatePricing.configured.value
        : baseUnitCents + addOnUnitCents;
      const baseLineCents = Number(item.final_line_price);
      const privateAddOnTransportIsValid = !usesPrivateAddOn
        || (addOnUnitCents === 0 && !legacyPricing.matched)
        || (
          legacyPricing.matched
          && legacyPricing.isValid
          && legacyPricing.addOnCents === addOnUnitCents
        );
      const lineIsValid = Number.isSafeInteger(quantity)
        && quantity >= 0
        && hasServerEvidence
        && serverIdentityMatches
        && (!serverRequiresCustomization || serverBcpoDataPresent)
        && (!serverRequiresCustomization || hasBcpoPricingEvidence)
        && (!serverRequiresConfiguredPriceState || usesPrivateAddOn)
        && (!hasBcpoPricingEvidence || serverBcpoDataPresent)
        && privatePricing.isValid
        && guardedColorPriceIsValid
        && (usesPrivateAddOn ? privatePricing.isValid : legacyPricing.isValid)
        && privateAddOnTransportIsValid
        && Number.isSafeInteger(baseUnitCents)
        && baseUnitCents >= 0
        && (!usesPrivateAddOn || baseUnitCents === authoritativeBaseUnitCents)
        && Number.isSafeInteger(addOnUnitCents)
        && Number.isSafeInteger(configuredUnitCents)
        && configuredUnitCents >= 0
        && baseUnitCents + addOnUnitCents === configuredUnitCents
        && Number.isSafeInteger(baseLineCents)
        && baseLineCents >= 0
        && baseLineCents + (addOnUnitCents * quantity) >= 0;

      if (isCustomized) customizedItemCount += 1;
      if (!lineIsValid) {
        isValid = false;
        validationErrors.push(`line-${index + 1}`);
      }

      const safeQuantity = Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : 0;
      const safeAddOnUnitCents = Number.isSafeInteger(addOnUnitCents)
        ? addOnUnitCents
        : 0;
      const safeBaseLineCents = Number.isSafeInteger(baseLineCents) && baseLineCents >= 0
        ? baseLineCents
        : 0;
      const addOnLineCents = safeAddOnUnitCents * safeQuantity;
      if (
        safeQuantity > 0
        && (
          safeAddOnUnitCents !== 0
          || (legacyPricing.isValid && legacyPricing.nonZeroMatchCount > 0)
        )
      ) {
        nonZeroAddOnLineCount += 1;
      }
      addOnSubtotalCents += addOnLineCents;
      lines.set(lineKey, {
        configuredLineCents: safeBaseLineCents + addOnLineCents,
        addOnLineCents,
        addOnUnitCents: safeAddOnUnitCents,
        isCustomized,
        pricingSource: usesPrivateAddOn ? 'private' : legacyPricing.matched ? 'legacy' : 'none',
        serverRequiresCustomization,
        serverRequiresConfiguredPriceState,
        serverBcpoDataPresent,
        guardedColorPriceRequired,
        guardedColorPriceIsValid
      });
    });

    const baseSubtotalCents = Number(cart && cart.total_price);
    if (!Number.isSafeInteger(baseSubtotalCents) || baseSubtotalCents < 0) {
      isValid = false;
      validationErrors.push('cart-total');
    }

    return {
      currency,
      lines,
      hasPriceAddOnProperties: customizedItemCount > 0,
      customizedItemCount,
      nonZeroAddOnLineCount,
      isValid,
      validationErrors,
      addOnSubtotalCents,
      configuredSubtotalCents: (
        Number.isSafeInteger(baseSubtotalCents) && baseSubtotalCents >= 0 ? baseSubtotalCents : 0
      ) + addOnSubtotalCents
    };
  };

  const updateCartPage = (cart, configured) => {
    const page = document.querySelector('[data-fd-cart-page]');
    if (!page) return;

    const byKey = new Map((cart.items || []).map((item) => [String(item.key || ''), item]));
    page.querySelectorAll('[data-fd-cart-item]').forEach((node) => {
      const key = String(node.dataset.fdCartKey || '');
      const item = byKey.get(key);
      const line = configured.lines.get(key);
      if (!item || !line) return;

      node.dataset.fdCartBaseUnitCents = String(Number(item.final_price) || 0);
      const linePrice = node.querySelector('[data-fd-cart-line-price]');
      if (linePrice) {
        linePrice.textContent = formatMoney(line.configuredLineCents, configured.currency);
        linePrice.dataset.fdConfiguredLineCents = String(line.configuredLineCents);
      }
    });

    const subtotal = page.querySelector('[data-fd-cart-subtotal]');
    if (subtotal) {
      subtotal.textContent = formatMoney(configured.configuredSubtotalCents, configured.currency);
      subtotal.dataset.fdConfiguredSubtotalCents = String(configured.configuredSubtotalCents);
    }
  };

  const updateMiniCart = (configured) => {
    document.querySelectorAll('[data-fd-cart-panel-item]').forEach((node) => {
      const line = configured.lines.get(String(node.dataset.fdCartPanelKey || ''));
      if (!line) return;
      const priceNodes = Array.from(node.querySelectorAll('strong'));
      const price = priceNodes[priceNodes.length - 1];
      if (price) {
        price.textContent = formatMoney(line.configuredLineCents, configured.currency);
        price.dataset.fdConfiguredLineCents = String(line.configuredLineCents);
      }
    });

    document.querySelectorAll('[data-fd-mini-cart-subtotal]').forEach((subtotal) => {
      subtotal.textContent = formatMoney(configured.configuredSubtotalCents, configured.currency);
      subtotal.dataset.fdConfiguredSubtotalCents = String(configured.configuredSubtotalCents);
      const label = subtotal.parentElement && subtotal.parentElement.querySelector('span');
      if (label && configured.addOnSubtotalCents !== 0) label.textContent = 'Configured subtotal';
    });

    document.querySelectorAll('.fd-cart-panel__assurance').forEach((list) => {
      const items = list.querySelectorAll('li');
      if (items[1]) items[1].textContent = 'Worldwide shipping can be arranged. U.S./Canada standard rates are shown at checkout; contact us before ordering for other destinations.';
      if (items[2]) items[2].textContent = 'Estimated U.S./Canada delivery: 7-10 business days after order confirmation';
    });
    document.querySelectorAll('.fd-cart-panel__notice').forEach((notice) => {
      notice.textContent = 'Configured subtotal includes selected add-ons. Review the current Shipping Policy. U.S./Canada standard rates, eligibility, discounts and tax are shown at checkout; contact us before ordering for other destinations.';
    });
  };

  const applyCart = (cart, serverEvidenceByLineKey) => {
    if (!cart || !Array.isArray(cart.items)) return;
    hasAppliedInitialCart = true;
    const configured = calculateConfiguredCart(cart, serverEvidenceByLineKey);
    activeConfiguredCart = configured;
    updateCartPage(cart, configured);
    updateMiniCart(configured);
    document.documentElement.dataset.fdConfiguredCartCurrency = configured.currency;
    document.documentElement.dataset.fdConfiguredCartAddOnCents = String(configured.addOnSubtotalCents);
    document.documentElement.dataset.fdConfiguredCartValid = String(configured.isValid);
  };

  const cartJsonUrl = () => {
    const root = String(window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/');
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}cart.js?fd_checkout=${Date.now()}`;
  };

  const cartHtmlUrl = () => {
    const root = String(window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/');
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}cart?fd_checkout_evidence=${Date.now()}`;
  };

  const getCart = () => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = window.setTimeout(() => {
      if (controller) controller.abort();
    }, CHECKOUT_TIMEOUT_MS);
    return fetch(cartJsonUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) throw new Error('Cart response was invalid.');
        return cart;
      })
      .finally(() => window.clearTimeout(timer));
  };

  const getFreshServerCartEvidence = () => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = window.setTimeout(() => {
      if (controller) controller.abort();
    }, CHECKOUT_TIMEOUT_MS);
    return fetch(cartHtmlUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'text/html' },
      signal: controller ? controller.signal : undefined
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart evidence request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.querySelector('script[data-fd-cart-server-evidence]')) {
          throw new Error('Cart server evidence was missing.');
        }
        return readServerCartEvidence(parsed);
      })
      .finally(() => window.clearTimeout(timer));
  };

  const showCheckoutError = (message, context) => {
    const text = message || CHECKOUT_ERROR_TEXT;
    const containers = [
      document.querySelector('.fd-cart-summary'),
      document.querySelector('.fd-cart-panel__checkout'),
      context && context.control && context.control.parentElement
    ].filter(Boolean);
    Array.from(new Set(containers)).forEach((container) => {
      let error = container.querySelector('[data-fd-bcpo-checkout-error]');
      if (!error) {
        error = document.createElement('p');
        error.setAttribute('data-fd-bcpo-checkout-error', '');
        error.setAttribute('role', 'alert');
        error.style.margin = '10px 0 0';
        error.style.padding = '10px 12px';
        error.style.border = '1px solid #a54b3d';
        error.style.borderRadius = '6px';
        error.style.background = '#fff4f1';
        error.style.color = '#7b2f24';
        error.style.fontSize = '12px';
        error.style.lineHeight = '1.4';
        container.appendChild(error);
      }
      error.textContent = text;
      error.hidden = false;
    });
    document.documentElement.dataset.fdBcpoCheckoutState = 'blocked';
    document.dispatchEvent(new CustomEvent('fd:bcpo-checkout-blocked', {
      detail: { reason: 'configured-price-not-confirmed' }
    }));
  };

  const clearCheckoutError = () => {
    document.querySelectorAll('[data-fd-bcpo-checkout-error]').forEach((error) => {
      error.hidden = true;
    });
  };

  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const waitForBcpo = () => new Promise((resolve, reject) => {
    const deadline = Date.now() + CHECKOUT_TIMEOUT_MS;
    const check = () => {
      const bcpoApi = window.bcpo;
      if (bcpoApi && bcpoApi.$ && typeof bcpoApi.$.ajax === 'function') {
        resolve(bcpoApi);
        return;
      }
      if (Date.now() >= deadline) {
        reject(new Error('BCPO checkout runtime was unavailable.'));
        return;
      }
      window.setTimeout(check, 100);
    };
    check();
  });

  const buildBcpoPayload = (cart, bcpoApi) => {
    const settings = window.bcpo_settings || {};
    const tags = Array.isArray(bcpoApi.tags) ? bcpoApi.tags.join(', ') : '';
    const noteField = document.querySelector('[name="note"]');
    const moneyFormat = typeof bcpoApi.getMoneyFormat === 'function'
      ? bcpoApi.getMoneyFormat()
      : '${{amount}}';
    return {
      shop: window.Shopify && window.Shopify.shop,
      cart,
      afDiscountObject: window.afDiscountObject,
      discountOnCartProApp: window.discountOnCartProApp
        ? window.discountOnCartProApp.integration
        : null,
      disallowDiscountCodesInCheckout: settings.disallowDiscountCodesInCheckout,
      selections: window.bcpoSelections,
      variant: window.bcpoVariant,
      note: noteField ? noteField.value : String(cart.note || ''),
      note_attributes: bcpoApi.note_attributes || {},
      tags,
      currency: window.Shopify && window.Shopify.currency && window.Shopify.currency.active,
      rate: window.Shopify && window.Shopify.currency && window.Shopify.currency.rate,
      shipping_address: bcpoApi.shipping_address,
      requires_shipping: settings.requires_shipping,
      money_format: moneyFormat,
      tax_price_addons: settings.tax_price_addons,
      langify: window.langify || window.TranslationLab ? 'on' : 'off',
      options_in_note: settings.options_in_note,
      shipping_title: settings.shipping_title,
      shipping_price: settings.shipping_price,
      properties_on_original: settings.properties_on_original,
      customer_id: window.__st && window.__st.cid
        ? `gid://shopify/Customer/${window.__st.cid}`
        : null
    };
  };

  const triggerBeforeCheckout = (bcpoApi) => {
    if (typeof bcpoApi.$ !== 'function') return;
    const result = bcpoApi.$(document).triggerHandler('bcpo:beforeCheckout');
    if (result) throw new Error('Checkout was cancelled by a BCPO integration.');
  };

  const callBcpoAddOnEndpoint = (payload, bcpoApi) => new Promise((resolve, reject) => {
    let request;
    try {
      request = bcpoApi.$.ajax({
        url: BCPO_ADD_ON_ENDPOINT,
        method: 'POST',
        type: 'POST',
        data: payload,
        dataType: 'text',
        timeout: CHECKOUT_TIMEOUT_MS
      });
    } catch (error) {
      reject(error);
      return;
    }
    if (!request || typeof request.done !== 'function' || typeof request.fail !== 'function') {
      reject(new Error('BCPO checkout request could not be started.'));
      return;
    }
    request
      .done((response) => resolve(response))
      .fail((xhr, status, error) => {
        const failure = new Error(String(error || status || 'BCPO checkout request failed.'));
        const httpStatus = Number(xhr && xhr.status) || 0;
        const statusText = String(status || '').toLowerCase();
        failure.status = httpStatus;
        failure.retryable = httpStatus === 0
          || statusText === 'timeout'
          || httpStatus >= 500;
        reject(failure);
      });
  });

  const responseUrlValue = (response) => {
    if (typeof response === 'string') {
      const text = response.trim();
      if (!text) return '';
      try {
        const parsed = JSON.parse(text);
        if (parsed !== response) return responseUrlValue(parsed);
      } catch (error) {
        // Plain URL responses are expected from the current BCPO service.
      }
      return text;
    }
    if (response && typeof response === 'object') {
      return String(response.checkout_url || response.checkoutUrl || response.url || '').trim();
    }
    return '';
  };

  const isTrustedCheckoutHost = (hostname) => {
    const host = String(hostname || '').toLowerCase();
    return TRUSTED_CHECKOUT_HOSTS.includes(host);
  };

  const parsedResponseObject = (response) => {
    if (response && typeof response === 'object') return response;
    if (typeof response !== 'string') return null;
    const text = response.trim();
    if (!text || !/^[{\[]/.test(text)) return null;
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const validateResponsePricing = (response, configured) => {
    const object = parsedResponseObject(response);
    if (!object) return;
    const centsKeys = [
      'configuredSubtotalCents',
      'configured_subtotal_cents',
      'lineTotalCents',
      'line_total_cents',
      'amountCents',
      'amount_cents'
    ];
    const amountKey = centsKeys.find((key) => Object.prototype.hasOwnProperty.call(object, key));
    if (amountKey) {
      const amount = Number(object[amountKey]);
      if (!Number.isSafeInteger(amount)
        || amount !== configured.configuredSubtotalCents) {
        throw new Error('BCPO checkout returned a mismatched configured amount.');
      }
    }
    if (Object.prototype.hasOwnProperty.call(object, 'currency')
      && String(object.currency || '').toUpperCase() !== configured.currency) {
      throw new Error('BCPO checkout returned a mismatched currency.');
    }
  };

  const validateCheckoutUrl = (response, configured) => {
    validateResponsePricing(response, configured);
    const value = responseUrlValue(response);
    if (!value) throw new Error('BCPO checkout returned an empty URL.');
    let url;
    try {
      url = new URL(value, window.location.origin);
    } catch (error) {
      throw new Error('BCPO checkout returned an invalid URL.');
    }
    const path = url.pathname.replace(/\/+/g, '/');
    const isBareNativeCheckout = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(path);
    const hasTokenizedCheckoutPath = /\/checkouts?\/[^/]+/i.test(path)
      || /\/c\/pay\/[^/]+/i.test(path)
      || /\/cart\/c\/[^/]+/i.test(path);
    const hasDraftOrderInvoicePath = /^\/\d+\/invoices\/[A-Za-z0-9_-]+\/?$/.test(path);
    if (url.protocol !== 'https:'
      || url.username
      || url.password
      || !isTrustedCheckoutHost(url.hostname)
      || isBareNativeCheckout
      || (!hasTokenizedCheckoutPath && !hasDraftOrderInvoicePath)) {
      throw new Error('BCPO checkout returned an untrusted URL.');
    }
    return url.href;
  };

  const requestBcpoCheckout = (payload, bcpoApi, configured) => {
    if (addOnRequestFlight) return addOnRequestFlight;
    const operation = (async () => {
      let lastError = null;
      for (let attempt = 1; attempt <= 2; attempt += 1) {
        if (attempt > 1) await wait(CHECKOUT_RETRY_DELAY_MS);
        document.documentElement.dataset.fdBcpoCheckoutAttempt = String(attempt);
        try {
          const response = await callBcpoAddOnEndpoint(payload, bcpoApi);
          return validateCheckoutUrl(response, configured);
        } catch (error) {
          lastError = error;
          if (attempt >= 2 || !error || error.retryable !== true) throw error;
        }
      }
      throw lastError || new Error('BCPO checkout request failed.');
    })();
    addOnRequestFlight = operation.finally(() => {
      addOnRequestFlight = null;
    });
    return addOnRequestFlight;
  };

  const setCheckoutBusy = () => {
    const snapshots = [];
    document.querySelectorAll('[name="checkout"], .fd-cart-panel__checkout, a[href]').forEach((control) => {
      const href = control.getAttribute && control.getAttribute('href');
      const isCheckoutControl = control.getAttribute && control.getAttribute('name') === 'checkout'
        || control.matches && control.matches('.fd-cart-panel__checkout')
        || href && isNativeCheckoutHref(href);
      if (!isCheckoutControl) return;
      snapshots.push({
        control,
        disabled: 'disabled' in control ? control.disabled : null,
        ariaBusy: control.getAttribute('aria-busy'),
        pointerEvents: control.style.pointerEvents,
        text: /^(BUTTON|A)$/i.test(control.tagName || '') ? control.textContent : null
      });
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.style.pointerEvents = 'none';
      if (/^(BUTTON|A)$/i.test(control.tagName || '')) {
        control.textContent = 'Confirming your custom price…';
      }
    });
    return () => snapshots.forEach((snapshot) => {
      if (!snapshot.control || !snapshot.control.isConnected) return;
      if (snapshot.disabled !== null) snapshot.control.disabled = snapshot.disabled;
      if (snapshot.ariaBusy === null) snapshot.control.removeAttribute('aria-busy');
      else snapshot.control.setAttribute('aria-busy', snapshot.ariaBusy);
      snapshot.control.style.pointerEvents = snapshot.pointerEvents;
      if (snapshot.text !== null) snapshot.control.textContent = snapshot.text;
    });
  };

  const nativeCheckoutPath = (pathname) => /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(
    String(pathname || '').replace(/\/+/g, '/')
  );

  function isNativeCheckoutHref(href) {
    try {
      const url = new URL(String(href || ''), window.location.origin);
      return url.origin === window.location.origin && nativeCheckoutPath(url.pathname);
    } catch (error) {
      return false;
    }
  }

  const continueNativeCheckout = (context) => {
    const form = context && context.form;
    if (form) {
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        form.appendChild(checkoutInput);
      }
      nativeCheckoutReplay = true;
      try {
        if (typeof form.requestSubmit === 'function') {
          const submitter = context.control
            && context.control.form === form
            && /^(BUTTON|INPUT)$/i.test(context.control.tagName || '')
            ? context.control
            : undefined;
          form.requestSubmit(submitter);
        } else {
          HTMLFormElement.prototype.submit.call(form);
        }
      } finally {
        window.setTimeout(() => { nativeCheckoutReplay = false; }, 0);
      }
      return;
    }

    const href = context && context.control && context.control.getAttribute('href');
    if (!isNativeCheckoutHref(href)) throw new Error('Native checkout destination was invalid.');
    nativeCheckoutReplay = true;
    try {
      context.control.click();
    } finally {
      window.setTimeout(() => { nativeCheckoutReplay = false; }, 0);
    }
  };

  const getCheckoutClickContext = (event) => {
    const target = event.target && event.target.closest
      ? event.target.closest('[name="checkout"], a[href], form.fd-cart-panel__checkout')
      : null;
    if (!target) return null;
    const name = target.getAttribute && target.getAttribute('name');
    if (name === 'checkout') {
      return {
        control: target,
        form: target.form || target.closest('form'),
        forceConfigured: false
      };
    }
    if (target.matches && target.matches('form.fd-cart-panel__checkout')) {
      return { control: target, form: target };
    }
    const href = target.getAttribute && target.getAttribute('href');
    return isNativeCheckoutHref(href) ? { control: target, form: null } : null;
  };

  const getCheckoutSubmitContext = (event) => {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form')) return null;
    const active = event.submitter || document.activeElement;
    const activeName = active && active.getAttribute && active.getAttribute('name');
    const dedicatedCheckoutForm = form.matches('.fd-cart-panel__checkout');
    if (active && active.form === form && activeName !== 'checkout' && !dedicatedCheckoutForm) return null;
    if (activeName !== 'checkout'
      && !dedicatedCheckoutForm
      && !form.querySelector('[name="checkout"]')) return null;
    return { control: active || form, form };
  };

  const startCheckout = (context) => {
    if (checkoutFlight) return checkoutFlight;
    const restoreBusy = setCheckoutBusy();
    clearCheckoutError();
    document.documentElement.dataset.fdBcpoCheckoutState = 'validating-cart';

    const operation = (async () => {
      try {
        const [cart, serverEvidenceByLineKey] = await Promise.all([
          getCart(),
          getFreshServerCartEvidence()
        ]);
        applyCart(cart, serverEvidenceByLineKey);
        const configured = activeConfiguredCart;
        if (!configured || !configured.isValid) {
          throw new Error('Configured cart pricing did not pass validation.');
        }

        if (configured.customizedItemCount === 0) {
          if (context && context.forceConfigured) {
            throw new Error('The custom checkout control did not match the refreshed cart.');
          }
          document.documentElement.dataset.fdBcpoCheckoutState = 'verified-native';
          continueNativeCheckout(context);
          return;
        }

        const bcpoApi = await waitForBcpo();
        triggerBeforeCheckout(bcpoApi);
        const payload = buildBcpoPayload(cart, bcpoApi);
        document.documentElement.dataset.fdBcpoCheckoutState = 'confirming-addons';
        const checkoutUrl = await requestBcpoCheckout(payload, bcpoApi, configured);
        document.documentElement.dataset.fdBcpoCheckoutState = 'redirecting';
        window.location.assign(checkoutUrl);
      } catch (error) {
        document.documentElement.dataset.fdBcpoCheckoutFailureStatus = String(
          error && (error.status || error.message) || 'failed'
        );
        showCheckoutError(CHECKOUT_ERROR_TEXT, context);
      } finally {
        restoreBusy();
      }
    })();

    checkoutFlight = operation.finally(() => {
      checkoutFlight = null;
    });
    return checkoutFlight;
  };

  const applyCartUpdate = (cart) => {
    applyCart(cart);
  };

  document.addEventListener('cart:updated', (event) => applyCartUpdate(event.detail && event.detail.cart));
  const isCartPage = () => Boolean(document.querySelector('[data-fd-cart-page]'))
    || /\/cart\/?$/.test(window.location.pathname);

  const run = () => {
    if (window.__fdCartSnapshot) {
      applyCartUpdate(window.__fdCartSnapshot);
      return;
    }
    if (!isCartPage()) return;

    if (hasAppliedInitialCart || window.__fdCartSnapshot) {
      if (!hasAppliedInitialCart && window.__fdCartSnapshot) applyCartUpdate(window.__fdCartSnapshot);
      return;
    }
    const initialCartRequest = typeof window.__fdGetCartShared === 'function'
      ? window.__fdGetCartShared()
      : getCart();
    initialCartRequest.then(applyCartUpdate).catch(() => {});
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
})();
